import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  CheckSquare, Clock, Calendar, MessageSquare, Edit, Trash2,
  Play, ChevronDown, ChevronRight, CornerDownRight
} from 'lucide-react';

const TaskMetaRow = ({ task, teamMembers, formatTime }) => (
  <div className="flex items-center gap-4 text-xs text-muted-foreground">
    {task.assigned_to && (
      <span>Assigned: {teamMembers.find((m) => m.user_id === task.assigned_to)?.name || 'Unknown'}</span>
    )}
    {task.end_date && (
      <span className="flex items-center gap-1">
        <Calendar className="h-3 w-3" />
        Due: {task.end_date}
      </span>
    )}
    {task.comments?.length > 0 && (
      <span className="flex items-center gap-1">
        <MessageSquare className="h-3 w-3" />
        {task.comments.length}
      </span>
    )}
    {task.total_tracked_time > 0 && (
      <span className="flex items-center gap-1">
        <Clock className="h-3 w-3" />
        {formatTime(task.total_tracked_time)}
      </span>
    )}
  </div>
);

const TaskStatusSelect = ({ task, statuses, onChange, compact }) => (
  <Select value={task.status} onValueChange={(v) => onChange(task.task_id, v)}>
    <SelectTrigger className={compact ? 'w-28 h-8 text-xs' : 'w-36'}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {statuses.map((status) => (
        <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const SubtaskRow = ({ subtask, activeTimer, onStart, onStatusChange, openDetail, getStatusBadge, statuses }) => (
  <div
    className="p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors flex items-center gap-3"
    onClick={() => openDetail(subtask)}
  >
    <CornerDownRight className="h-4 w-4 text-muted-foreground" />
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <span className="font-medium text-sm">{subtask.title}</span>
        {getStatusBadge(subtask.status)}
      </div>
    </div>
    <div
      className="flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {!activeTimer && (
        <Button size="sm" variant="ghost" onClick={() => onStart(subtask.task_id)} title="Start Timer" className="h-7 w-7 p-0">
          <Play className="h-3 w-3" />
        </Button>
      )}
      <TaskStatusSelect task={subtask} statuses={statuses} onChange={onStatusChange} compact />
    </div>
  </div>
);

const TaskRow = ({
  task, subtasks, expanded, setExpanded, isAdmin, selectedTaskIds, onToggleSelect,
  activeTimer, onStart, onStatusChange, openDetail, getStatusBadge, getPriorityBadge,
  teamMembers, formatTime, statuses
}) => (
  <div>
    <div
      className="p-4 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => openDetail(task)}
      data-testid={`task-item-${task.task_id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 flex items-start gap-3">
          {isAdmin && (
            <div className="pt-1" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={selectedTaskIds.includes(task.task_id)}
                onCheckedChange={() => onToggleSelect(task.task_id)}
              />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {subtasks.length > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => ({ ...prev, [task.task_id]: !prev[task.task_id] }));
                  }}
                  className="p-1 hover:bg-muted rounded"
                >
                  {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              )}
              <h4 className="font-medium">{task.title}</h4>
              {getStatusBadge(task.status)}
              {getPriorityBadge(task.priority)}
              {subtasks.length > 0 && (
                <span className="text-xs text-muted-foreground">({subtasks.length} subtasks)</span>
              )}
            </div>
            {task.description && (
              <p className="text-sm text-muted-foreground line-clamp-1 mb-2">{task.description}</p>
            )}
            <TaskMetaRow task={task} teamMembers={teamMembers} formatTime={formatTime} />
          </div>
        </div>
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {!activeTimer && (
            <Button size="sm" variant="ghost" onClick={() => onStart(task.task_id)} title="Start Timer">
              <Play className="h-4 w-4" />
            </Button>
          )}
          <TaskStatusSelect task={task} statuses={statuses} onChange={onStatusChange} />
        </div>
      </div>
    </div>

    {expanded && subtasks.length > 0 && (
      <div className="ml-8 mt-2 space-y-2">
        {subtasks.map((subtask) => (
          <SubtaskRow
            key={subtask.task_id}
            subtask={subtask}
            activeTimer={activeTimer}
            onStart={onStart}
            onStatusChange={onStatusChange}
            openDetail={openDetail}
            getStatusBadge={getStatusBadge}
            statuses={statuses}
          />
        ))}
      </div>
    )}
  </div>
);

const BulkActionsBar = ({ count, onEdit, onDelete, onClear }) => (
  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg mb-4" data-testid="bulk-actions-bar">
    <span className="text-sm font-medium">{count} selected</span>
    <Button size="sm" variant="outline" onClick={onEdit} data-testid="bulk-edit-btn">
      <Edit className="mr-1 h-3 w-3" /> Edit
    </Button>
    <Button size="sm" variant="destructive" onClick={onDelete} data-testid="bulk-delete-btn">
      <Trash2 className="mr-1 h-3 w-3" /> Delete
    </Button>
    <Button size="sm" variant="ghost" onClick={onClear}>Clear</Button>
  </div>
);

export const ProjectTaskList = ({
  parentTasks, subtasksByParent, expandedTasks, setExpandedTasks,
  isAdmin, selectedTaskIds, onToggleSelect, onToggleAll, onBulkEdit, onBulkDelete, onClearSelection,
  activeTimer, onStart, onStatusChange, openDetail, getStatusBadge, getPriorityBadge,
  teamMembers, formatTime, statuses
}) => (
  <Card className="p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-heading font-semibold">Tasks ({parentTasks.length})</h3>
      {isAdmin && parentTasks.length > 0 && (
        <div className="flex items-center gap-2">
          <Checkbox
            checked={selectedTaskIds.length === parentTasks.length && parentTasks.length > 0}
            onCheckedChange={onToggleAll}
            data-testid="select-all-tasks"
          />
          <span className="text-xs text-muted-foreground">Select All</span>
        </div>
      )}
    </div>

    {isAdmin && selectedTaskIds.length > 0 && (
      <BulkActionsBar
        count={selectedTaskIds.length}
        onEdit={onBulkEdit}
        onDelete={onBulkDelete}
        onClear={onClearSelection}
      />
    )}

    {parentTasks.length === 0 ? (
      <div className="text-center py-8">
        <CheckSquare className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No tasks yet. Create your first task to get started.</p>
      </div>
    ) : (
      <div className="space-y-3">
        {parentTasks.map((task) => (
          <TaskRow
            key={task.task_id}
            task={task}
            subtasks={subtasksByParent[task.task_id] || []}
            expanded={expandedTasks[task.task_id]}
            setExpanded={setExpandedTasks}
            isAdmin={isAdmin}
            selectedTaskIds={selectedTaskIds}
            onToggleSelect={onToggleSelect}
            activeTimer={activeTimer}
            onStart={onStart}
            onStatusChange={onStatusChange}
            openDetail={openDetail}
            getStatusBadge={getStatusBadge}
            getPriorityBadge={getPriorityBadge}
            teamMembers={teamMembers}
            formatTime={formatTime}
            statuses={statuses}
          />
        ))}
      </div>
    )}
  </Card>
);
